import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { createContext } from "use-context-selector";
import Loader from "../component/Loader";
import { AnimatePresence, motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import { isEmpty } from "lodash";
import { apiGet, apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import jwt_decode from "jwt-decode";
const ThemeContext = createContext();
export default ThemeContext;

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [cssLoaded, setCssLoaded] = useState(false);
  useEffect(() => {
    const loadCss = async () => {
      const isExcludedRoute = [
        "sports-velki",
        "match-detail-velki",
        "league-velki",
        "sports-sky",
        "inplay-sky",
        "match-detail-sky",
        "horse-racing-sky",
        "favourite-velkie",
        "favourite-sky",
        "sports-sky-new",
        "account-sky",
        "exchange",
        "exchange-velki",
        "exchange",
        "customer-support",
        "result-sky",
      ].includes(location.pathname?.split("/")[1]);

      if (isExcludedRoute) {
        for (let i = 0; i <= 9; i++) {
          const id =
            i === 0 ? "__SVG_SPRITE_NODE__" : `__SVG_SPRITE_NODE__${i}`;
          const el = document.getElementById(id);
          if (el) el.remove();
        }
      }
      document.body.dataset.bdtwinUi = isExcludedRoute ? "exchange" : "portal";
      document.body.dataset.bdtwinDevice = !isExcludedRoute || !isMobile ? "desktop" : "mobile";
      setCssLoaded(false);
      document
        .querySelectorAll("link[data-theme]")
        .forEach((link) => link.remove());
      const addStylesheet = (href) =>
        new Promise((resolve, reject) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = href;
          link.setAttribute("data-theme", theme);
          link.onload = resolve;
          link.onerror = () => reject(`❌ Failed: ${href}`);
          document.head.appendChild(link);
        });

      if (!isExcludedRoute) {
        document.body.className = "";
        const themePromises = [
          addStylesheet("/assets/index2.css"),
          addStylesheet("/assets/Account.css"),
          addStylesheet("/assets/WebAccount.css"),
          addStylesheet("/assets/animation.css"),
        ];

        await Promise.all(themePromises);
      }

      if (
        [
          "sports-velki",
          "match-detail-velki",
          "league-velki",
          "favourite-velkie",
          "exchange-velki",
        ].includes(location.pathname?.split("/")[1])
      ) {
        if (!isMobile) {
          document.body.className = "is-desktop";
        }
        document.documentElement.className =
          "canvas webgl supports csstransforms csstransforms3d avif apng webp s-webp no-jpeg2000 webp-animation webp-alpha webp-lossless";
        document.documentElement.style.setProperty(
          "--root-font-size",
          isMobile ? "3.7333333333333334vw" : "17.92px"
        );

        const themePromises = [
          addStylesheet("/assets/velkie/velkie.css"),
          addStylesheet("/assets/velkie/velkieMedia.css"),
          addStylesheet("/assets/icomoon.css"),
        ];

        await Promise.all(themePromises);
      } else if (
        [
          "sports-sky",
          "match-detail-sky",
          "inplay-sky",
          "horse-racing-sky",
          "favourite-sky",
          "sports-sky-new",
          "account-sky",
          "exchange",'result-sky'
        ].includes(location.pathname?.split("/")[1])
      ) {
        if (isMobile) {
          document.body.className = "overfloxScroll";
        }
        const themePromises = [
          addStylesheet(
            ["exchange"].includes(location.pathname?.split("/")[1])
              ? "/assets/Sky/web.css"
              : isMobile
              ? "/assets/Sky/sky.css"
              : "/assets/Sky/web.css"
          ),
          addStylesheet("/assets/icomoon.css"),
          ...(["result-sky"].includes(location.pathname?.split("/")[1]) &&
          isMobile
            ? [addStylesheet("/assets/Sky/result.css")]
            : []),
        ];

        await Promise.all(themePromises);
      }

      await addStylesheet("/assets/bdtwin-theme.css");
    await addStylesheet("/assets/bdtwin-match.css?v=invite-popup-112");
    await addStylesheet("/assets/responsive.css?v=responsive-28");

      setCssLoaded(true);
    };

    loadCss().catch(console.error);
  }, [theme, location.pathname]);

  const toggleTheme = () => {
    setCssLoaded(false);
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const [banner, setBanner] = useState([]);
  const [message, setMessageList] = useState([]);
  const messageList = async () => {
    const { status, data: response_users } = await apiGet(apiPath.messageList);
    if (status === 200) {
      if (response_users.success) {
        setMessageList(response_users.results);
      }
    }
  };

  let getBannerData = async () => {
    let user = localStorage.getItem("token")
      ? jwt_decode(localStorage.getItem("token"))
      : "";
    const data = await apiPost(apiPath.getBanner, {
      type: "home_top",
      userId: !isEmpty(user) ? user?.user?._id : "",
    });
    if (data?.status == 200) {
      if (data?.data?.success) {
        setBanner(data?.data?.results?.banners);
      }
    }
  };
  useEffect(() => {
    getBannerData();
    messageList();
  }, []);
  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, toggleTheme, banner, message }}
    >
      {![
        "sports-velki",
        "match-detail-velki",
        "league-velki",
        "sports-sky",
        "inplay-sky",
        "match-detail-sky",
        "horse-racing-sky",
        "favourite-velkie",
        "favourite-sky",
        "sports-sky-new",
        "account-sky",
        "exchange",
        "exchange-velki",
      ].includes(location.pathname?.split("/")[1]) && (
        <AnimatePresence mode="wait">
          {!cssLoaded && <Loader />}
        </AnimatePresence>
      )}
      {cssLoaded && (
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {children}{" "}
          </motion.div>
        </AnimatePresence>
      )}
    </ThemeContext.Provider>
  );
};
